import _every from "lodash/every";
import _find from "lodash/find";
import _first from "lodash/first";
import _get from "lodash/get";
import _has from "lodash/has";
import _isEmpty from "lodash/isEmpty";
import _pickBy from "lodash/pickBy";
import _size from "lodash/size";
import _some from "lodash/some";
import _values from "lodash/values";
import { getCheckoutSubscriptions, State as CheckoutState } from "./checkout";
import { FINANCING_TYPE, FinancingCode, FinancingType, LEGAL_OWNER_MIN_AGE } from "../../../../../constants";
import {
    ICartFinancing,
    ICartSize,
    ICartSubscriptionPrice,
    ICartSubscriptionProductUser,
    ICartSubscriptionReswitch,
    ICartType,
    OfferingCode,
    SIMDEAL_CAMPAIGN_CODE,
} from "../../../../../models";
import { CheckoutSubscription, SubscriptionsState } from "../../../../../models/state";
import {
    isBroadbandCode,
    isHandsetWithLeaseCode,
    isMbbCommitmentCode,
} from "../../../../../shared/subscriptionCodeUtils";

export type State = CheckoutState;
export const getSubscriptions: (state: State) => SubscriptionsState = (state) => getCheckoutSubscriptions(state);
export const hasSubscriptions: (state: State) => boolean = (state) => _size(getSubscriptions(state)) > 0;

/** @deprecated get rid of isLegal */
export const getLegalSubscription: (state: State) => CheckoutSubscription | undefined = (state) => {
    const subscriptions = getSubscriptions(state);
    return _first(_values(subscriptions));
};

/** @deprecated get rid of isLegal */
export const getLegalSubscriptionId: (state: State) => string | undefined = (state) =>
    _get(getLegalSubscription(state), "id");

export const isSubscriptionKeepExistingReSwitching: (state: State) => boolean = (state) => {
    // when ordering re-svitsj - we only have ONE subscription in the cart
    // meaning we can look up the first subscription id and use it
    const subscriptions = getSubscriptions(state);

    if (_size(subscriptions) > 0) {
        const reSwitchObj = _find(subscriptions, (subscription) => {
            return _has(subscription, "reswitch.ban") && _has(subscription, "reswitch.imei");
        });

        return !!reSwitchObj;
    }

    return false;
};

// Fetch subscription based on id. If no id is provided we use legal subscription
export const getSubscription: (state: State, subscriptionId?: string) => CheckoutSubscription | undefined = (
    state,
    subscriptionId,
) => _get(getSubscriptions(state), subscriptionId || "");

export const isLegalSubscription: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getLegalSubscription(state)?.id === subscriptionId;

export const isEsimSelected: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "isEsimSelected") === true;

export const isSimFromStoreSelected: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "isSimFromStoreSelected") === true;

export const getSubscriptionMaxAgeAllowed: (state: State, subscriptionId?: string) => number | undefined = (
    state,
    subscriptionId,
) => (getSubscription(state, subscriptionId) || {}).maxAgeAllowed;

export const isProductUserMandatory: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) => {
    const maxAge = getSubscriptionMaxAgeAllowed(state, subscriptionId);
    return maxAge ? maxAge < LEGAL_OWNER_MIN_AGE : false;
};

export const getSubscriptionCode: (state: State, subscriptionId?: string) => OfferingCode = (state, subscriptionId) =>
    (getSubscription(state, subscriptionId) || {}).code || "";

export const getSubscriptionName: (state: State, subscriptionId?: string) => string = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "name", "");

export const getSubscriptionPrice: (state: State, subscriptionId?: string) => Partial<ICartSubscriptionPrice> = (
    state,
    subscriptionId,
) => _get(getSubscription(state, subscriptionId), "price", {});

export const getSubscriptionSize: (state: State, subscriptionId?: string) => Partial<ICartSize> = (
    state,
    subscriptionId,
) => _get(getSubscription(state, subscriptionId), "size", {});

export const getSubscriptionType: (state: State, subscriptionId?: string) => ICartType | "" = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "type", "");

export const hasSubscriptionDataUnlimited: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => _get(getSubscription(state, subscriptionId), "isDataUnlimited", false);

export const getSubscriptionNumberOfBytes: (state: State, subscriptionId?: string) => string = (
    state,
    subscriptionId,
) => (getSubscription(state, subscriptionId) || {}).numberOfBytes || " din maks antall";

export const isSubscriptionPrepaid: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).startsWith("KONTANT");

export const isSubscriptionBroadband: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    isBroadbandCode(getSubscriptionCode(state, subscriptionId) as OfferingCode);

export const isSubscriptionSimDeal: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).indexOf(SIMDEAL_CAMPAIGN_CODE) > -1;

export const isSubscriptionHandsetCommitment: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => isHandsetWithLeaseCode(getSubscriptionCode(state, subscriptionId) as OfferingCode);

export const hasHandsetLease = (state: State): boolean =>
    _some(getSubscriptions(state), (subscription) => isHandsetWithLeaseCode(subscription.code as OfferingCode));

export const isSubscriptionMbbCommitment: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => isMbbCommitmentCode(getSubscriptionCode(state, subscriptionId) as OfferingCode);

export const isSubscription10PercentDiscount: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => getSubscriptionCode(state, subscriptionId).indexOf("COM12PURP10") > -1;

export const isCatalogListingOk: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "isCatalogListingOk") === true;

export const isSecretNumber: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "isSecretNumber") === true;

export const isPhoneNumberSelectedAtPDP: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "isPhoneNumberSelectedAtPDP") === true;

export const isProductUserDifferentThanLegalOwner: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => _get(getSubscription(state, subscriptionId), "isProductUserDifferentThanLegalOwner") === true;

export const isSubscriptionKeepExisting: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    _get(getSubscription(state, subscriptionId), "keepExistingSubscription") === true;

export const isServiceSubscription = (state: State, subscriptionId?: string) =>
    !!getSubscription(state, subscriptionId)?.isServiceSubscription;

export const isExistingPrepaidKBDSubscription: (state: State, subscriptionId?: string) => boolean | undefined = (
    state,
    subscriptionId,
) => (getSubscription(state, subscriptionId) || {}).isExistingPrepaidKBDSubscription;

const getSubscriptionFinancing: (state: State, subscriptionId?: string) => Partial<ICartFinancing> = (
    state,
    subscriptionId,
) => (getSubscription(state, subscriptionId) || {}).financing || {};

export const getSubscriptionFinancingDuration = (state: State, subscriptionId: string): number =>
    parseFloat(`${getSubscriptionFinancing(state, subscriptionId).duration || 0}`);

export const getSubscriptionFinancingType = (state: State, subscriptionId?: string): FinancingType | undefined =>
    getSubscriptionFinancing(state, subscriptionId).type;

export const getSubscriptionFinancingCode: (state: State, subscriptionId: string) => FinancingCode | undefined = (
    state,
    subscriptionId,
) => getSubscriptionFinancing(state, subscriptionId).code;

export const isChildSubscription: (state: State, subscriptionId: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("BARN");

export const isJuniorSubscription: (state: State, subscriptionId: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("JUNIOR");

export const isUngSubscription: (state: State, subscriptionId: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("_UNG");

export const isTeliaTryggSubscription: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("TRYGG");

export const isTeliaTrySubscription: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("TELIA_X_INTRO"); // TODO: Replace with correct code once ready

export const isCampaignSubscription: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    getSubscriptionCode(state, subscriptionId).includes("TELIA_X_KAMPANJE_2026_2");

// Product User
export const getProductUser: (state: State, subscriptionId?: string) => Partial<ICartSubscriptionProductUser> = (
    state,
    subscriptionId,
) => (getSubscription(state, subscriptionId) || {}).productUser || {};

export const hasProductUserData: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    !!(getSubscription(state, subscriptionId) || {}).productUser;

export const getProductUserFullName: (state: State, subscriptionId?: string) => string = (state, subscriptionId) =>
    `${_get(getProductUser(state, subscriptionId), "firstName", "")} ${_get(
        getProductUser(state, subscriptionId),
        "lastName",
        "",
    )}`.trim();

export const getProductUserFullCity: (state: State, subscriptionId?: string) => string = (state, subscriptionId) =>
    `${_get(getProductUser(state, subscriptionId), "postcode", "")} ${_get(
        getProductUser(state, subscriptionId),
        "city",
        "",
    )}`.trim();

export const getProductUserAddress: (state: State, subscriptionId?: string) => string = (state, subscriptionId) =>
    getProductUser(state, subscriptionId).street || "";

// reswitch
export const getReSwitch: (state: State, subscriptionId?: string) => ICartSubscriptionReswitch | undefined = (
    state,
    subscriptionId,
) => getSubscription(state, subscriptionId)?.reswitch;

export const isReSwitch: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    !!_get(getReSwitch(state, subscriptionId), "imei", "");

export const isSwitchReplacementAtSubscriptionLevel: (state: State, subscriptionId?: string) => boolean = (
    state,
    subscriptionId,
) => !!_get(getReSwitch(state, subscriptionId), "phoneNumber", "");

export const getInitiatedByWhenReSwitch: (state: State, subscriptionId?: string) => string = (state, subscriptionId) =>
    _get(getReSwitch(state, subscriptionId), "initiatedBy", "webshop");

const switchTypes: (FinancingType | undefined)[] = [FINANCING_TYPE.SWITCH, FINANCING_TYPE.RESWITCH];
const leaseTypes: (FinancingType | undefined)[] = [FINANCING_TYPE.LEASE];
export const isSwitchOrReSwitch: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    switchTypes.includes(getSubscriptionFinancingType(state, subscriptionId));

export const isLease: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    leaseTypes.includes(getSubscriptionFinancingType(state, subscriptionId));

export const getTotalRemainingMonths: (state: State, subscriptionId?: string) => boolean = (state, subscriptionId) =>
    !!_get(getReSwitch(state, subscriptionId), "totalRemainingMonths", null);

export const hasOnlyPrepaidSubscriptions: (state: State) => boolean = (state) => {
    const subscriptions = getSubscriptions(state);
    return (
        !_isEmpty(subscriptions) &&
        _every(subscriptions, (subscription) => (_get(subscription, "code") || "").startsWith("KONTANT"))
    );
};

export const hasTransferSubscriptionFromOtherOwner: (state: State) => boolean = (state) =>
    _some(getSubscriptions(state), (subscription) =>
        _get(subscription, "phoneNumber.transferSubscriptionFromOtherOwner", false),
    );

// TODO: This function gives error when used in useSelector due to immutability issue.
//       Consider doing the filtering in comp and remove this instead.
export const getMobileSubscriptions: (state: State) => SubscriptionsState = (state) =>
    _pickBy(getCheckoutSubscriptions(state), (sub, id) => !isSubscriptionBroadband(state, id));

export const getChangedServices = (state: State, subscriptionId: string) =>
    getSubscription(state, subscriptionId)?.changedServices || [];

export const getOldSubscription = (state: State, subscriptionId: string) =>
    getSubscription(state, subscriptionId)?.oldSubscription;

export const getNumberSelectedAtPDP = (state: State) => {
    const subscriptions = getSubscriptions(state);
    const subscription = _find(_values(subscriptions), (sub) => sub.isPhoneNumberSelectedAtPDP === true);
    return subscription?.phoneNumber?.selectedPhoneNumber;
};

export const getSelectedPortinDate = (state: State, subscriptionId?: string) =>
    getSubscription(state, subscriptionId)?.requestedPortingDate;
